/**
 * Global Search Dialog — Cmd+/ to search across all REDTAPE entities.
 *
 * Debounced search, result list with type icons, confidence badges,
 * click-to-navigate via deep links.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  X,
  Building2,
  FileText,
  Users,
  Target,
  BookOpen,
  Briefcase,
} from 'lucide-react'
import { api } from '@/lib/api'

interface SearchResult {
  id: string
  type: 'customer' | 'opportunity' | 'document' | 'kg_node' | 'knowledge_chunk' | 'stakeholder'
  title: string
  snippet: string
  confidence: number
  relevanceNote: string
  url: string
}

const TYPE_ICONS: Record<string, typeof Building2> = {
  customer: Building2,
  opportunity: Target,
  document: FileText,
  knowledge_chunk: BookOpen,
  stakeholder: Users,
  kg_node: Briefcase,
}

const TYPE_LABELS: Record<string, string> = {
  customer: 'Customer',
  opportunity: 'Opportunity',
  document: 'Document',
  knowledge_chunk: 'Knowledge',
  stakeholder: 'Stakeholder',
  kg_node: 'KG Node',
}

const TYPE_COLORS: Record<string, string> = {
  customer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  opportunity: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  document: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  knowledge_chunk: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  stakeholder: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  kg_node: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
}

export function GlobalSearchDialog() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [meta, setMeta] = useState<{ candidateCount: number; durationMs: number; reranked: boolean; cached: boolean } | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const navigate = useNavigate()

  // Cmd+/ to open
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
    } else {
      setQuery('')
      setResults([])
      setMeta(null)
      setSelectedIndex(0)
    }
  }, [open])

  // Debounced search
  const doSearch = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults([])
      setMeta(null)
      return
    }
    setLoading(true)
    try {
      const response = await api.search.global(q)
      setResults(response.results)
      setMeta({
        candidateCount: response.candidateCount,
        durationMs: response.durationMs,
        reranked: response.reranked,
        cached: response.cached,
      })
      setSelectedIndex(0)
    } catch (err) {
      console.error('[GlobalSearch] Search failed:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleInputChange = useCallback((value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(value), 300)
  }, [doSearch])

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault()
      navigate(results[selectedIndex].url)
      setOpen(false)
    }
  }, [results, selectedIndex, navigate])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Dialog */}
      <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search customers, documents, stakeholders..."
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
          />
          {loading && (
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
          )}
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto">
          {results.length === 0 && query.length >= 2 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No results found for "{query}"
            </div>
          )}

          {results.map((result, i) => {
            const Icon = TYPE_ICONS[result.type] || FileText
            return (
              <button
                key={`${result.type}:${result.id}`}
                onClick={() => {
                  navigate(result.url)
                  setOpen(false)
                }}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                  i === selectedIndex
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                } ${i > 0 ? 'border-t border-gray-100 dark:border-gray-800' : ''}`}
              >
                <Icon className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {result.title}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${TYPE_COLORS[result.type] || ''}`}>
                      {TYPE_LABELS[result.type]}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {result.relevanceNote || result.snippet}
                  </p>
                </div>
                <span className="text-[10px] font-mono text-gray-400 flex-shrink-0 mt-0.5">
                  {Math.round(result.confidence * 100)}%
                </span>
              </button>
            )
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            {meta && (
              <>
                <span>{meta.candidateCount} candidates</span>
                <span>{meta.durationMs}ms</span>
                {meta.reranked && <span className="text-blue-400">AI reranked</span>}
                {meta.cached && <span className="text-green-400">cached</span>}
              </>
            )}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-400">
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 font-mono">↑↓</kbd>
            <span>navigate</span>
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 font-mono">↵</kbd>
            <span>open</span>
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 font-mono">esc</kbd>
            <span>close</span>
          </div>
        </div>
      </div>
    </div>
  )
}
